import * as React from "react";
import { useAppDispatch } from "../../redux/hooks";
import { authenticate } from "../../redux/reducers/user-slice";
import { userApi } from "../../api";
import { isAxiosError } from "axios";
import { NavLink, useNavigate } from "react-router";
import { INVITED_BASE_URL } from "../../common";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { SubmitHandler, useForm } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

type Inputs = {
  email: string;
  displayName: string;
  password: string;
};

enum SignUpErrors {
  EMAIL_ALREADY_IN_USE = "EMAIL_ALREADY_IN_USE",
}

export default ({ onLogInClick }: { onLogInClick?: VoidFunction }) => {
  const dispatch = useAppDispatch();
  const nav = useNavigate();
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [emailUsed, setEmailUsed] = React.useState<string>("");
  const hcaptchaRef = React.createRef<HCaptcha>();
  const [token, setToken] = React.useState<string>("");

  React.useEffect(() => {
    setFocus("email");
  }, []);

  const {
    register,
    handleSubmit,
    setFocus,
    setError,
    formState: { errors },
  } = useForm<Inputs>();

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    if (!token) {
      setError("root", { message: "Captcha must be completed." });
      return;
    }

    setSubmitting(true);
    userApi
      .signUp(data.displayName, data.email, data.password, token)
      .then(() => {
        dispatch(authenticate()).then(() => {
          nav("/");
        });
      })
      .catch((err) => {
        if (isAxiosError(err)) {
          if (err.response?.data.error === SignUpErrors.EMAIL_ALREADY_IN_USE) {
            setError("email", { message: "This email is already in use." });
            setEmailUsed(data.email);
          } else {
            setError("root", {
              message: "An unknown error occured. Please try again later.",
            });
            setEmailUsed("");
          }
        } else {
          setError("root", {
            message: "An unknown error occured. Please try again later.",
          });
          setEmailUsed("");
        }
        hcaptchaRef.current?.resetCaptcha();
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const _onLogInClick = () => {
    if (onLogInClick) {
      onLogInClick();
    } else {
      nav(`/login${emailUsed ? `?email=${emailUsed}` : ""}`);
    }
  };

  return (
    <div className="w-80">
      <div className="text-[#333] text-center font-semibold text-2xl mb-6">
        Join Invited today
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="w-full">
          <div>
            <Label htmlFor="email" className="text-xs">
              EMAIL
            </Label>
            <Input
              type="email"
              id="email"
              className="rounded-xs"
              {...register("email", {
                required: "Email is required.",
                pattern: {
                  value: /^.+@.+\..+$/,
                  message: "Email is required.",
                },
              })}
            />
            {errors.email && (
              <p className="text-[#e30000] text-xs mt-1">
                {errors.email.message}
              </p>
            )}
          </div>
          <div className="mt-4">
            <Label htmlFor="displayname" className="text-xs">
              DISPLAY NAME
            </Label>
            <Input
              type="text"
              id="displayname"
              className="rounded-xs"
              {...register("displayName", {
                required: "Display name is required.",
                pattern: {
                  value: /^[^\s]+$/,
                  message: "",
                },
                max: 32,
              })}
            />
            {errors.displayName && (
              <p className="text-[#e30000] text-xs mt-1">
                {errors.displayName.message}
              </p>
            )}
          </div>
          <div className="mt-4">
            <Label htmlFor="password" className="text-xs">
              PASSWORD
            </Label>
            <Input
              type="password"
              id="password"
              className="rounded-xs"
              {...register("password", {
                required: "Password is required.",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters long.",
                },
                maxLength: {
                  value: 32,
                  message: "Password can be a maximum of 32 characters long.",
                },
              })}
            />
            {errors.password && (
              <p className="text-[#e30000] text-xs mt-1">
                {errors.password?.message}
              </p>
            )}
          </div>
          <div className="mt-4">
            <HCaptcha
              sitekey={process.env.HCAPTCHA_SITE_KEY!}
              onVerify={setToken}
              ref={hcaptchaRef}
            />
          </div>
          <Button
            type="submit"
            className="mt-4 w-full rounded-sm"
            disabled={submitting}
          >
            {submitting ? <Loader2 className="animate-spin" /> : "Sign Up"}
          </Button>
          {errors.root && (
            <p className="text-[#e30000] text-xs mt-1">{errors.root.message}</p>
          )}
          <div className="text-xs mt-1 text-[#333]">
            <span>{"By registering, you agree to Invited's "}</span>
            <a
              href={`${INVITED_BASE_URL}/terms`}
              target="_blank"
              className="hover:underline"
            >
              Terms of Service
            </a>
            <span> and </span>
            <a
              href={`${INVITED_BASE_URL}/privacy`}
              target="_blank"
              className="hover:underline"
            >
              Privacy Policy
            </a>
            <span>.</span>
          </div>
          <div className="text-sm text-[#333] mt-2">
            <span className="">Already have an account? </span>
            <span
              className="hover:underline cursor-pointer"
              onClick={_onLogInClick}
            >
              Log In
            </span>
          </div>
        </div>
      </form>
    </div>
  );
};
